import type { QuickFilterDef, QuickFilterValue } from './FilterBar.props';

/**
 * Accessors for the config-driven quick-filter slots.
 *
 * Slot values live in `FilterState.quick`, keyed by `QuickFilterDef.key`. That
 * bag is `Record<string, string | string[]>`, which a page's `buildFilterModel`
 * would otherwise have to narrow at every read. These two never throw and
 * always return the shape the caller asked for, so the ~25 read sites stay
 * one-liners.
 */

/** A multiselect slot's values. Tolerates a single string so a slot that
 *  changed kind, or state seeded from a URL, degrades instead of crashing. */
export const quickList = (
  quick: Record<string, QuickFilterValue> | undefined,
  key: string
): string[] => {
  const value = quick?.[key];
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
};

/** A text slot's value. Takes the first entry if handed a list, for the same
 *  reason. */
export const quickText = (
  quick: Record<string, QuickFilterValue> | undefined,
  key: string
): string => {
  const value = quick?.[key];
  if (Array.isArray(value)) return value[0] ?? '';
  return value ?? '';
};

/**
 * The all-empty slot bag for a config: `[]` per multiselect, `''` per text.
 *
 * Pages build their `EMPTY_FILTERS` from this rather than hand-writing the bag,
 * so adding a slot to the config cannot leave its initial value undefined.
 */
export const emptyQuickState = (
  defs: readonly QuickFilterDef[]
): Record<string, QuickFilterValue> =>
  Object.fromEntries(defs.map((d) => [d.key, d.kind === 'multiselect' ? [] : '']));
